import { ScheduleItemDto } from "../features/calendar/calendar.types";
import i18n from "../i18n";

export const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export const swap = (array: any[], fromIndex: number, toIndex: number) => {
  const newArray = array.slice();
  const [movedItem] = newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, movedItem);
  return newArray;
};

export const getTextColorForBackground = (hexColor: string): string => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Perceived luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.58 ? "#000000" : "#ffffff";
};

export const parseRecurrence = (
  recurrence: string | string[],
): string[] | null => {
  if (Array.isArray(recurrence)) {
    return recurrence;
  }
  ////
  switch (recurrence) {
    case "none":
      return null;
    case "daily":
      return ["RRULE:FREQ=DAILY"];
    case "weekly":
      return ["RRULE:FREQ=WEEKLY"];
    case "monthly":
      return ["RRULE:FREQ=MONTHLY"];
    case "yearly":
      return ["RRULE:FREQ=YEARLY"];
    default:
      return null;
  }
};

/*
const parseRFC5545ToRecurrence = (recurrence: string | string[]): string => {
  if (!recurrence) return "none";

  const rule = Array.isArray(recurrence) ? recurrence[0] : recurrence;

  if (rule.includes("FREQ=DAILY")) return "daily";
  if (rule.includes("FREQ=WEEKLY")) return "weekly";
  if (rule.includes("FREQ=MONTHLY")) return "monthly";
  if (rule.includes("FREQ=YEARLY")) return "yearly";

  return "none";
};
*/

// Function to format a date-time string for input[type="datetime-local"]
export const formatDateTimeLocal = (dateTime: string | number | Date) => {
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return ""; // Handle invalid dates gracefully

  const offset = date.getTimezoneOffset() * 60000; // Adjust for local timezone
  const localDate = new Date(date.getTime() - offset);
  return localDate.toISOString().slice(0, 16); // Returns YYYY-MM-DDTHH:mm
};

/**
 * "YYYY-MM-DD" of the LOCAL calendar day, for date inputs of values stored
 * as local start / end of day (task due dates, list and project dates).
 * formatDateInput uses UTC instead - right for all-day schedule items stored
 * at UTC midnight, but a local midnight shows as the day before there.
 */
export const formatLocalDateInput = (dateInput: string | number | Date) =>
  dateInput ? formatDateTimeLocal(dateInput).slice(0, 10) : "";

/**
 * Same for deadlines (list, project, milestone ends): the day of the moment
 * just before. Older deadlines were saved as the next day's midnight, new
 * ones as 23:59:59.999 - both show the intended day this way.
 */
export const formatLocalEndDateInput = (dateInput: string | number | Date) =>
  dateInput ? formatLocalDateInput(new Date(dateInput).getTime() - 1) : "";

export const formatAllDayDateToISO = (
  dateInput: string | number | Date,
): string => {
  const date = new Date(dateInput);

  // Construct a new date using UTC values to prevent timezone shift
  // Create a new Date set to midnight in local time, then convert to ISO.

  const newDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      0,
      0,
      0,
    ),
  );
  return newDate.toISOString();
};

export const dayNames = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const fullDayNames = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Full weekday names (localized)
export function getFullWeekdayNames(): string[] {
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
  });
  // Reference Monday–Sunday week
  const baseDate = new Date(2024, 0, 1); // Jan 1, 2024 = Monday
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + i);
    return formatter.format(date);
  });
}

export function getShortWeekdayNames(): string[] {
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    weekday: "short",
  });
  // Create a reference week (Monday–Sunday)
  const baseDate = new Date(2024, 0, 1); // Jan 1, 2024 (a Monday)
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + i);
    return formatter.format(date);
  });
}

export const monthNames = [
  "January",
  "February",
  "March",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatWeekdayAndDate(
  date: Date,
  locale: string,
  timeZone?: string,
) {
  const opts = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  } as const;
  return new Intl.DateTimeFormat(locale, opts).format(date);
}

/**
 * A stable identity for one rendered calendar item. Expanded occurrences of a
 * recurring series all share the master's `id`, so keying a list by `id` alone
 * collapses the whole series into a single React child — which then reuses one
 * set of gesture/spring state across every occurrence.
 */
export function getOccurrenceKey(event: ScheduleItemDto): string {
  return `${event.id}::${event.originalStart ?? event.scheduleStart}`;
}

/**
 * True when the recurrence rules pin the series to particular weekdays or
 * dates (BYDAY, BYMONTHDAY, ...). For those, shifting the master's DTSTART by
 * a day does not move the occurrences — the BY* part still decides where they
 * land — so a cross-day drag has to be refused rather than silently ignored.
 * Rules produced by `parseRecurrence` carry no BY* part and shift fine.
 */
export function recurrenceIsPinnedToCalendarDays(
  recurrence: string[] | undefined,
): boolean {
  if (!recurrence || recurrence.length === 0) return false;
  return recurrence.some((rule) =>
    /BY(DAY|MONTHDAY|YEARDAY|WEEKNO|MONTH|SETPOS)=/i.test(rule),
  );
}

export const getDurationString = (hours: number, minutes: number) => {
  return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
};

export const getTimeFromDurationString = (time: string) => {
  const [hours, minutes, seconds] = time
    .split(":")
    .map((val) => parseInt(val) || 0);
  return { hours, minutes, seconds };
};

export const getDayOfTheWeek = (date: Date) => {
  return date.getDay() === 0 ? 7 : date.getDay();
};

export const getIsDuringDayInUTC = (
  date: Date | string,
  relevantDate?: Date,
) => {
  const eventDate = new Date(date.toString());

  // `relevantDate` is a local calendar day (e.g. a `weekdays` array entry or
  // `new Date()` for "today"), so its UTC bounds must be derived from its
  // *local* Y/M/D, not its UTC Y/M/D — otherwise the range shifts to the
  // wrong day whenever local midnight falls on a different UTC date.
  const reference = relevantDate || new Date();
  const startDate = new Date(
    Date.UTC(
      reference.getFullYear(),
      reference.getMonth(),
      reference.getDate(),
      0,
      0,
      0,
      0,
    ),
  );
  const endDate = new Date(
    Date.UTC(
      reference.getFullYear(),
      reference.getMonth(),
      reference.getDate(),
      23,
      59,
      59,
      999,
    ),
  );

  return eventDate >= startDate && eventDate <= endDate;
};

/**
 * @description Is
 * @param date
 * @param relevantDate
 * @returns
 */
export const getIsDuringDay = (date: Date | string, relevantDate?: Date) => {
  const eventDate = new Date(date.toString());

  // Get current date in the local time zone
  const reference = relevantDate || new Date();
  const startDate = new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate(),
    0,
    0,
    0,
    0,
  );
  const endDate = new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate(),
    23,
    59,
    59,
    999,
  );

  return eventDate >= startDate && eventDate <= endDate;
};

export function partition<T>(
  array: T[],
  predicate: (item: T) => boolean,
): [T[], T[]] {
  return array.reduce<[T[], T[]]>(
    ([pass, fail], item) => {
      return predicate(item)
        ? [[...pass, item], fail]
        : [pass, [...fail, item]];
    },
    [[], []],
  );
}

export const eventActive = (
  eventStart: Date | string,
  eventEnd: Date | string,
) => {
  const now = Date.now();

  const start = new Date(eventStart).getTime();
  const end = new Date(eventEnd).getTime();

  if (isNaN(start) || isNaN(end)) {
    throw new Error("Invalid date input");
  }

  // Elapsed seconds

  // Is active
  if (start < now && now < end) {
    return true;
  } else return false;
};

export const durationToEvent = (
  eventStart: Date | string,
  eventEnd: Date | string,
) => {
  const now = Date.now();

  const start = new Date(eventStart).getTime();
  const end = new Date(eventEnd).getTime();

  if (isNaN(start) || isNaN(end)) {
    throw new Error("Invalid date input");
  }

  // Elapsed seconds

  // Is active
  if (start < now && now < end) {
    return 0;
  }
  // Is in future (start > now)
  else if (start > now) {
    return now - start;
  }
  // Is in past (now > end)
  else {
    return now - end;
  }
};
